import { create } from 'zustand';
import type { Device } from '@cornerstone/shared';
import { API_BASE } from '../client/config';

export type GroupRole = 'Owner' | 'Admin' | 'Editor' | 'Viewer';
export type ApprovalMode = 'central' | 'group' | 'auto';
export type DeviceGroup = {
  id: string;
  orgId: string;
  name: string;
  approvalMode: ApprovalMode;
  policyVersion: number;
  /** 서버는 항상 채워 준다. 테스트 fixture 호환을 위해 선택 필드. */
  accentColor?: string | null;
  logoDataUrl?: string | null;
  deviceCount: number;
  memberCount: number;
  /** 그룹이 소유한 미디어·플레이리스트·슬라이드·폴더 합계. 0이어야 그룹을 지울 수 있다. */
  contentCount?: number;
  /** 진행 중(대기·승인) 요청 수. 0이어야 그룹을 지울 수 있다. */
  openRequestCount?: number;
};
export type GroupBranding = { accentColor?: string | null; logoDataUrl?: string | null };
export type GroupDevice = Device & { groupId: string | null };
export type GroupActor = {
  orgId: string;
  /** 서버 ScopeActor의 사용자 id. 관리 이력에서 "내가 한 일"을 알아보는 데 쓴다(S17).
   *  테스트 fixture 호환을 위해 선택 필드. */
  userId?: string | null;
  role: GroupRole | null;
  central: boolean;
  enforced: boolean;
  groups: Array<{ groupId: string; role: GroupRole }>;
};
type GroupList = { groups: DeviceGroup[]; devices: GroupDevice[]; actor: GroupActor };
type GroupPatch = { name?: string; approvalMode?: ApprovalMode; policyVersion: number } & GroupBranding;

type DeviceGroupsState = {
  groups: DeviceGroup[];
  devices: GroupDevice[];
  actor: GroupActor | null;
  loading: boolean;
  pending: boolean;
  error: string | null;
  load: () => Promise<void>;
  /** 조용한 재조회 — loading 플래그를 올리지 않아 폴링에 써도 화면이 잠기지 않는다. 변경 중이면 건너뛴다. */
  refresh: () => Promise<void>;
  createGroup: (input: { name: string; approvalMode?: ApprovalMode } & GroupBranding) => Promise<boolean>;
  updateGroup: (id: string, patch: GroupPatch) => Promise<boolean>;
  deleteGroup: (id: string, mode?: 'transfer' | 'purge') => Promise<boolean>;
  assignDevice: (deviceId: string, groupId: string | null) => Promise<boolean>;
};

export function createDeviceGroupsStore(orgId?: string) {
  const apiUrl = (path = '') =>
    `${API_BASE}/api/device-groups${path}${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`;
  let loadVersion = 0;
  // 2026-09-16 검수 S2(b) — 목록(GET /api/device-groups)이 한 주기에 두 번 연속
  // (0~4ms 간격) 나가고 있었다. 같은 스토어를 쓰는 화면이 겹쳐 뜨거나 load()와
  // 폴링 refresh()가 같은 틱에 겹치면 서버에 똑같은 질의를 두 번 던진다.
  // 이미 날아간 요청이 있으면 그 응답을 함께 쓴다 — 결과는 어차피 같다.
  let inflightList: Promise<GroupList> | null = null;

  async function request(path: string, method = 'GET', body?: unknown): Promise<Response> {
    const response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      ...(body === undefined ? {} : {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => null) as { error?: string; message?: string } | null;
      throw new Error(detail?.message || detail?.error || `요청 실패 (HTTP ${response.status})`);
    }
    return response;
  }

  /** 목록 조회. fresh=false(폴링)는 이미 날아간 요청에 올라타고, fresh=true(명시적
   *  load — 변경 직후 재조회 포함)는 반드시 새로 읽는다. 변경 전에 출발한 응답을
   *  재사용하면 방금 만든 그룹이 목록에서 빠져 보이기 때문. */
  function readList(fresh: boolean): Promise<GroupList> {
    if (!fresh && inflightList) return inflightList;
    const pending: Promise<GroupList> = (async () => (await request('')).json() as Promise<GroupList>)()
      .finally(() => { if (inflightList === pending) inflightList = null; });
    inflightList = pending;
    return pending;
  }

  return create<DeviceGroupsState>((set, get) => {
    async function mutate(path: string, method: string, body?: unknown): Promise<boolean> {
      if (get().pending || get().loading) return false;
      set({ pending: true, error: null });
      try {
        await request(path, method, body);
        await get().load();
        return true;
      } catch (error) {
        set({ error: error instanceof Error ? error.message : String(error) });
        return false;
      } finally {
        set({ pending: false });
      }
    }

    return {
      groups: [],
      devices: [],
      actor: null,
      loading: false,
      pending: false,
      error: null,
      load: async () => {
        const version = ++loadVersion;
        set({ loading: true, error: null });
        try {
          const data = await readList(true);
          if (version === loadVersion) set({ ...data, loading: false });
        } catch (error) {
          if (version === loadVersion) {
            set({
              groups: [], devices: [], actor: null, loading: false,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
      },
      refresh: async () => {
        if (get().pending || get().loading) return;
        const version = ++loadVersion;
        try {
          const data = await readList(false);
          if (version === loadVersion && !get().pending) set({ ...data });
        } catch {
          // 폴링 실패는 조용히 넘긴다 — 다음 주기나 명시적 새로고침에서 다시 시도.
        }
      },
      createGroup: (input) => mutate('', 'POST', { name: input.name.trim(), ...(input.approvalMode ? { approvalMode: input.approvalMode } : {}),
        ...(input.accentColor !== undefined ? { accentColor: input.accentColor } : {}), ...(input.logoDataUrl !== undefined ? { logoDataUrl: input.logoDataUrl } : {}) }),
      updateGroup: (id, patch) => mutate(`/${encodeURIComponent(id)}`, 'PATCH', patch),
      // 남은 것이 있어도 화면이 처리 방식을 골라 보낸다 — 여기서 미리 막지 않는다. 막을 이유는
      // 서버가 한국어로 돌려준다(2026-09-18 대교: "삭제 시도를 할 수 있어야 할 것").
      deleteGroup: async (id, mode) => {
        if (!get().groups.some((item) => item.id === id)) { set({ error: '그룹을 찾을 수 없습니다.' }); return false; }
        // 쿼리로 보내지 않는다 — apiUrl 이 뒤에 ?orgId= 를 덧붙여 ?mode=x?orgId=y 로 깨진다.
        return mutate(`/${encodeURIComponent(id)}`, 'DELETE', mode ? { mode } : undefined);
      },
      assignDevice: (id, groupId) => mutate(`/devices/${encodeURIComponent(id)}`, 'PUT', { groupId }),
    };
  });
}

export const useDeviceGroupsStore = createDeviceGroupsStore();
